import { useRef, useEffect, useState } from "react";
import { useSurveyStore } from "@src/store/survey";
import { CheckboxGroup, Flex, Text, Grid } from "@radix-ui/themes";
import { useMutation } from "@tanstack/react-query";
import { recordResponse } from "@api/survey";
import PropTypes from "prop-types";
import isEqual from "lodash.isequal"; // Import a utility for deep comparison

TopChoicesQuestion.propTypes = {
  question: PropTypes.object,
  uuid: PropTypes.string,
  hint_text: PropTypes.string,
  options: PropTypes.object,
};

export default function TopChoicesQuestion({ question }) {
  const [prevChoices, setPrevChoices] = useState([]);
  const sessionUUID = useSurveyStore((state) => state.sessionUUID);
  const surveyResponses = useSurveyStore((state) => state.surveyResponses);
  const setSurveyResponses = useSurveyStore(
    (state) => state.setSurveyResponses,
  );
  const [choices, setChoices] = useState([]);
  const uuid = useRef(null);

  const response = useMutation({
    mutationFn: recordResponse,
    onSuccess: (data) => {
      setSurveyResponses((state) => {
        return { ...state.surveyResponses, [question.uuid]: data };
      });
    },
    onError: (error) => {
      console.error(error);
    },
  });

  useEffect(() => {
    uuid.current = self.crypto.randomUUID();
  }, []);

  useEffect(() => {
    if (question && surveyResponses[question.uuid]?.response?.choices) {
      setChoices(surveyResponses[question.uuid].response.choices);
      setPrevChoices(surveyResponses[question.uuid].response.choices);
    }
  }, [question, question.uuid, surveyResponses]);

  useEffect(() => {
    if (choices && !isEqual(choices, prevChoices)) {
      response.mutate({
        answerUUID: uuid.current,
        response: {
          choices: choices,
        },
        sessionUUID: sessionUUID,
        surveyQuestionUUID: question.uuid,
      });
      setPrevChoices(choices);
    }
  }, [
    choices,
    prevChoices,
    question.uuid,
    question.options.choices,
    response,
    sessionUUID,
  ]);

  if (!choices) return null;

  return (
    <Flex direction="column">
      <Text as="p" size="3" mb="2" weight="bold">
        {question.question}
      </Text>
      <Text as="p" size="1" color="gray" mb="2">
        {question.hint_text}
      </Text>
      <CheckboxGroup.Root value={choices} onValueChange={setChoices}>
        <Grid columns="1" rows="repeat(2)" width="auto">
          {question.options.options.map((option, idx) => (
            <CheckboxGroup.Item
              value={option.label}
              key={idx}
              disabled={
                choices.length >= question.options.choices &&
                !choices.includes(option.label)
              }
            >
              {option.label}
            </CheckboxGroup.Item>
          ))}
        </Grid>
      </CheckboxGroup.Root>
    </Flex>
  );
}
